import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Drawer,
  IconButton,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import HomeOutlinedIcon from '@mui/icons-material/HomeOutlined';
import AddCommentOutlinedIcon from '@mui/icons-material/AddCommentOutlined';
import ViewSidebarOutlinedIcon from '@mui/icons-material/ViewSidebarOutlined';
import CloseIcon from '@mui/icons-material/Close';
import { authFetchInit, hasAuthSession } from '../utils/authSession';
import { API_ROOT } from '../config/apiConfig';
import { snapshotHasValues, type VysorSessionSnapshot } from '../utils/vysorSessionSnapshot';
import VysorContextPanel from '../components/vysor-lab/VysorContextPanel';
import VysorMessageList, {
  type LabChatMessage,
  type LabQuickOption,
} from '../components/vysor-lab/VysorMessageList';
import VysorComposer from '../components/vysor-lab/VysorComposer';
import VysorStarterGrid from '../components/vysor-lab/VysorStarterGrid';
import {
  LAB,
  LAB_STARTERS,
  labCanvas,
  labHairline,
  labInk,
  labMuted,
  labRailBg,
  labSurface,
} from '../components/vysor-lab/vysorLabTheme';

const API = `${API_ROOT}/assistant`;

function uid(): string {
  return `${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
}

/**
 * Preview full-bleed VYSOR agent UI.
 * Not linked from the sidebar — open `/vysor-lab` directly.
 */
const VysorAgentLabPage: React.FC = () => {
  const navigate = useNavigate();
  const muiTheme = useTheme();
  const isMobile = useMediaQuery(muiTheme.breakpoints.down('md'));
  const mode: 'light' | 'dark' = muiTheme.palette.mode === 'dark' ? 'dark' : 'light';

  const canvas = labCanvas(mode);
  const ink = labInk(mode);
  const muted = labMuted(mode);
  const hairline = labHairline(mode);
  const railBg = labRailBg(mode);
  const surface = labSurface(mode);

  const [messages, setMessages] = useState<LabChatMessage[]>([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [conversationId, setConversationId] = useState<string | undefined>();
  const [snapshot, setSnapshot] = useState<VysorSessionSnapshot | null>(null);
  const [starters, setStarters] = useState(LAB_STARTERS);
  const [panelOpen, setPanelOpen] = useState(false);
  const [desktopPanel, setDesktopPanel] = useState(true);
  const initializedRef = useRef(false);

  const isHome = messages.length === 0;
  const hasContext = snapshotHasValues(snapshot);

  const activeOptionsMessageId = useMemo(() => {
    for (let i = messages.length - 1; i >= 0; i -= 1) {
      const m = messages[i];
      if (m.role === 'bot' && m.options?.length) return m.id;
    }
    return null;
  }, [messages]);

  const addBot = useCallback((content: string, options?: LabQuickOption[]) => {
    setMessages((prev) => [...prev, { id: uid(), role: 'bot', content, options }]);
  }, []);

  const addUser = useCallback((content: string) => {
    setMessages((prev) => [...prev, { id: uid(), role: 'user', content }]);
  }, []);

  const initChat = useCallback(async () => {
    setMessages([]);
    setConversationId(undefined);
    setSnapshot(null);
    setInput('');
    setStarters(LAB_STARTERS);
    setLoading(true);
    try {
      if (!hasAuthSession()) {
        addBot('I need you to be logged in to help you. Please log in and try again.');
        return;
      }
      const res = await fetch(`${API}/quick-start`, authFetchInit());
      const data = await res.json();
      const payload = data?.data || data || {};
      if (payload.conversationId || data.conversationId) {
        setConversationId(payload.conversationId || data.conversationId);
      }
      // Map quick-start options onto starter cards when they match known goals
      if (Array.isArray(payload.options) && payload.options.length) {
        const mapped = payload.options
          .map((o: { id?: string; label?: string; description?: string }) => {
            const id = String(o.id || '');
            const known = LAB_STARTERS.find((s) => s.id === id);
            if (known) return known;
            if (!id || !o.label) return null;
            return {
              id,
              label: String(o.label),
              description: String(o.description || 'Continue with VYSOR'),
              prompt: String(o.label),
            };
          })
          .filter(Boolean) as typeof LAB_STARTERS;
        if (mapped.length) setStarters(mapped.slice(0, 4));
      }
    } catch {
      // Home still works with default starters
    } finally {
      setLoading(false);
    }
  }, [addBot]);

  useEffect(() => {
    if (initializedRef.current) return;
    initializedRef.current = true;
    void initChat();
  }, [initChat]);

  // Auto-open context when values appear
  useEffect(() => {
    if (hasContext && !isMobile) setDesktopPanel(true);
  }, [hasContext, isMobile]);

  const callChat = useCallback(
    async (message: string, optionId?: string) => {
      const res = await fetch(
        `${API}/chat`,
        authFetchInit({
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            message,
            conversationId,
            optionId: optionId || undefined,
            debugState: true,
          }),
        }),
      );
      const data = await res.json();
      if (!res.ok || data?.success === false) {
        throw new Error(data?.error || `Chat failed (${res.status})`);
      }
      if (data.conversationId) setConversationId(data.conversationId);
      const payload = data.data || {};
      if (payload.debugState && typeof payload.debugState === 'object') {
        setSnapshot(payload.debugState as VysorSessionSnapshot);
      }
      const reply = String(payload.message || '').trim() || 'Done.';
      const options = Array.isArray(payload.options)
        ? payload.options
            .map((o: { id?: string; label?: string; description?: string }) => ({
              id: String(o.id || o.label || ''),
              label: String(o.label || o.id || ''),
              description: o.description,
            }))
            .filter((o: LabQuickOption) => o.id && o.label)
        : undefined;
      addBot(reply, options);
    },
    [addBot, conversationId],
  );

  const sendMessage = useCallback(
    async (text: string, optionId?: string) => {
      const msg = text.trim();
      if (!msg || loading) return;
      setInput('');
      addUser(msg);
      setLoading(true);
      try {
        await callChat(msg, optionId);
      } catch (err: unknown) {
        const detail = err instanceof Error && err.message ? err.message : '';
        addBot(
          detail
            ? `I ran into an error: ${detail}. You can retry or start a new chat.`
            : 'I ran into an error. You can retry or start a new chat.',
        );
      } finally {
        setLoading(false);
      }
    },
    [addUser, addBot, callChat, loading],
  );

  const onOption = useCallback(
    (opt: LabQuickOption) => {
      void sendMessage(opt.label, opt.id);
    },
    [sendMessage],
  );

  const onStarter = useCallback(
    (s: { id: string; label: string; prompt: string }) => {
      void sendMessage(s.prompt, s.id.startsWith('start_') ? s.id : undefined);
    },
    [sendMessage],
  );

  const showDesktopPanel = !isMobile && desktopPanel;

  const railBtn = (opts: {
    title: string;
    onClick: () => void;
    icon: React.ReactNode;
    active?: boolean;
    disabled?: boolean;
  }) => (
    <Tooltip title={opts.title} placement="right">
      <span>
        <IconButton
          onClick={opts.onClick}
          disabled={opts.disabled}
          aria-label={opts.title}
          sx={{
            width: 40,
            height: 40,
            color: opts.active ? ink : muted,
            bgcolor: opts.active
              ? mode === 'dark'
                ? 'rgba(255,255,255,0.08)'
                : 'rgba(0,0,0,0.06)'
              : 'transparent',
            '&:hover': {
              bgcolor: mode === 'dark' ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.05)',
            },
          }}
        >
          {opts.icon}
        </IconButton>
      </span>
    </Tooltip>
  );

  return (
    <Box
      sx={{
        height: '100vh',
        width: '100vw',
        display: 'flex',
        overflow: 'hidden',
        bgcolor: canvas,
        fontFamily: LAB.fontBody,
        backgroundImage:
          mode === 'dark'
            ? 'radial-gradient(900px 480px at 50% -10%, rgba(255,255,255,0.04), transparent 60%)'
            : 'radial-gradient(900px 480px at 50% -8%, rgba(0,0,0,0.03), transparent 55%)',
      }}
    >
      {/* Left icon rail — Claude / ChatGPT pattern */}
      <Box
        sx={{
          width: LAB.rail,
          flexShrink: 0,
          display: { xs: 'none', sm: 'flex' },
          flexDirection: 'column',
          alignItems: 'center',
          py: 1.5,
          gap: 0.75,
          borderRight: `1px solid ${hairline}`,
          bgcolor: railBg,
        }}
      >
        <Box
          sx={{
            width: 32,
            height: 32,
            borderRadius: '10px',
            bgcolor: ink,
            color: surface,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontWeight: 700,
            fontSize: '0.85rem',
            letterSpacing: '-0.04em',
            mb: 1,
          }}
        >
          V
        </Box>
        {railBtn({
          title: 'New chat',
          onClick: () => void initChat(),
          icon: <AddCommentOutlinedIcon sx={{ fontSize: 20 }} />,
          disabled: loading,
        })}
        {railBtn({
          title: 'Dashboard',
          onClick: () => navigate('/dashboard'),
          icon: <HomeOutlinedIcon sx={{ fontSize: 20 }} />,
        })}
        <Box sx={{ flex: 1 }} />
        {railBtn({
          title: desktopPanel ? 'Hide context' : 'Show context',
          onClick: () => setDesktopPanel((v) => !v),
          icon: <ViewSidebarOutlinedIcon sx={{ fontSize: 20 }} />,
          active: desktopPanel,
        })}
      </Box>

      {/* Main column */}
      <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', minWidth: 0 }}>
        {/* Slim top bar (mobile + title) */}
        <Box
          sx={{
            flexShrink: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            px: { xs: 1.5, md: 2.5 },
            py: 1.1,
            borderBottom: isHome ? 'none' : `1px solid ${hairline}`,
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Box sx={{ display: { xs: 'flex', sm: 'none' }, gap: 0.25 }}>
              <IconButton size="small" onClick={() => void initChat()} sx={{ color: muted }}>
                <AddCommentOutlinedIcon fontSize="small" />
              </IconButton>
              <IconButton size="small" onClick={() => navigate('/dashboard')} sx={{ color: muted }}>
                <HomeOutlinedIcon fontSize="small" />
              </IconButton>
            </Box>
            {!isHome && (
              <Typography
                sx={{
                  fontFamily: LAB.fontBody,
                  fontWeight: 650,
                  fontSize: '0.95rem',
                  letterSpacing: '-0.02em',
                  color: ink,
                }}
              >
                VYSOR
              </Typography>
            )}
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <Typography
              sx={{
                fontFamily: LAB.fontBody,
                fontSize: '0.65rem',
                fontWeight: 600,
                letterSpacing: '0.06em',
                textTransform: 'uppercase',
                color: muted,
                display: { xs: 'none', md: 'block' },
              }}
            >
              Preview
            </Typography>
            {isMobile && (
              <IconButton size="small" onClick={() => setPanelOpen(true)} sx={{ color: muted }}>
                <ViewSidebarOutlinedIcon fontSize="small" />
              </IconButton>
            )}
          </Box>
        </Box>

        {isHome ? (
          /* Home: Claude-style centered greeting + composer + starters */
          <Box
            sx={{
              flex: 1,
              overflow: 'auto',
              display: 'flex',
              flexDirection: 'column',
              px: { xs: 2, sm: 3 },
              py: { xs: 3, md: 4 },
            }}
          >
            <Box
              sx={{
                flex: 1,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                width: '100%',
                maxWidth: LAB.column,
                mx: 'auto',
                gap: 3,
                animation: 'labHomeIn 500ms cubic-bezier(0.22, 1, 0.36, 1)',
                '@keyframes labHomeIn': {
                  from: { opacity: 0, transform: 'translateY(12px)' },
                  to: { opacity: 1, transform: 'none' },
                },
              }}
            >
              <Box sx={{ textAlign: 'center' }}>
                <Typography
                  sx={{
                    fontFamily: LAB.fontBody,
                    fontWeight: 700,
                    fontSize: { xs: '1.85rem', md: '2.35rem' },
                    letterSpacing: '-0.04em',
                    color: ink,
                    lineHeight: 1.15,
                  }}
                >
                  How can I help you today?
                </Typography>
                <Typography
                  sx={{
                    mt: 1.25,
                    fontFamily: LAB.fontBody,
                    fontSize: '0.95rem',
                    color: muted,
                    lineHeight: 1.5,
                  }}
                >
                  SSO, applications, and access — guided by VYSOR
                </Typography>
              </Box>

              <Box sx={{ width: '100%' }}>
                <VysorComposer
                  value={input}
                  loading={loading}
                  mode={mode}
                  elevated
                  autoFocus
                  placeholder="Ask VYSOR anything…"
                  onChange={setInput}
                  onSend={() => void sendMessage(input)}
                />
              </Box>

              <Box sx={{ width: '100%', mt: 0.5 }}>
                <VysorStarterGrid
                  mode={mode}
                  starters={starters}
                  disabled={loading}
                  onPick={onStarter}
                />
              </Box>
            </Box>
          </Box>
        ) : (
          <>
            <VysorMessageList
              messages={messages}
              loading={loading}
              activeOptionsMessageId={activeOptionsMessageId}
              mode={mode}
              onOption={onOption}
            />
            <Box
              sx={{
                flexShrink: 0,
                px: { xs: 2, sm: 3 },
                pb: { xs: 2.25, md: 3 },
                pt: 1,
                background: `linear-gradient(180deg, transparent 0%, ${canvas} 32%)`,
              }}
            >
              <VysorComposer
                value={input}
                loading={loading}
                mode={mode}
                onChange={setInput}
                onSend={() => void sendMessage(input)}
              />
            </Box>
          </>
        )}
      </Box>

      {/* Context / artifacts panel */}
      {showDesktopPanel && (
        <Box
          sx={{
            width: LAB.artifacts,
            flexShrink: 0,
            height: '100%',
            borderLeft: `1px solid ${hairline}`,
            bgcolor: canvas,
          }}
        >
          <VysorContextPanel snapshot={snapshot} mode={mode} />
        </Box>
      )}

      <Drawer
        anchor="right"
        open={isMobile && panelOpen}
        onClose={() => setPanelOpen(false)}
        PaperProps={{ sx: { width: 'min(100vw, 380px)', bgcolor: canvas } }}
      >
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', px: 1, pt: 1 }}>
          <IconButton size="small" onClick={() => setPanelOpen(false)} sx={{ color: muted }}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>
        <VysorContextPanel snapshot={snapshot} mode={mode} dense />
      </Drawer>
    </Box>
  );
};

export default VysorAgentLabPage;
